import React, { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './ClientOnboardingForm.css';

const nichesList = [
  'Beauty', 'Fitness', 'Food & Drink', 'Tech', 'SaaS', 'Healthcare', 'Education', 'Finance', 'Other'
];

export default function ClientOnboarding({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [accountType, setAccountType] = useState(''); // 'agency' or 'brand'
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [website, setWebsite] = useState('');
  const [productService, setProductService] = useState('');
  const [hearAboutUs, setHearAboutUs] = useState('');
  const [workedWithCreators, setWorkedWithCreators] = useState('');
  const [monthlyBudget, setMonthlyBudget] = useState('');
  const [goals, setGoals] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setLoading(false), 1000);
  }, []);

  const handleGoalToggle = (goal) => {
    setGoals((prev) =>
      prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal]
    );
  };

  const showToast = (message, type = 'error') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'error' });
    }, 4000);
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      if (!accountType) {
        showToast('Please select if you are an agency or brand');
        return;
      }
    }
    
    if (currentStep === 2) {
      if (!name.trim()) {
        showToast('Please enter your name');
        return;
      }
      if (!brand.trim()) {
        showToast(`Please enter your ${accountType === 'agency' ? 'agency' : 'brand'} name`);
        return;
      }
      if (accountType === 'brand' && !productService.trim()) {
        showToast('Please enter your main product or service');
        return;
      }
    }
    
    if (currentStep === 3) {
      if (!hearAboutUs) {
        showToast('Please let us know how you heard about us');
        return;
      }
      if (!workedWithCreators) {
        showToast('Please let us know if you have worked with creators before');
        return;
      }
    }
    
    if (currentStep === 4) {
      if (!monthlyBudget) {
        showToast('Please select your monthly budget range');
        return;
      }
      if (goals.length === 0) {
        showToast('Please select at least one goal');
        return;
      }
    }

    if (currentStep === 5) {
      // Profile image step - must upload an image
      if (!profileImage) {
        showToast('Please upload a profile image to continue');
        return;
      }
      // Final step - submit
      await handleSubmit();
      return;
    }

    setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setUploading(true);
    setError('');

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('User not logged in');

      let imageUrl = '/images/profile-placeholder.svg';

      if (profileImage) {
        const imageRef = ref(storage, `profileImages/${user.uid}`);
        await uploadBytes(imageRef, profileImage);
        imageUrl = await getDownloadURL(imageRef);
      }

      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        name,
        brandName: brand,
        accountType: accountType, // 'agency' or 'brand'
        website: website || '',
        productService: accountType === 'brand' ? productService : '',
        hearAboutUs,
        workedWithCreators,
        monthlyBudget,
        goals,
        profileImage: imageUrl,
        userType: 'client',
        onboardingStatus: {
          completed: true,
          callScheduled: false,
          callCompleted: false,
          onboardingCompleted: true,
        },
        createdAt: new Date().toISOString(),
      }, { merge: true });

      if (onComplete) onComplete();
    } catch (err) {
      setError(err.message);
      showToast(err.message);
    } finally {
      setUploading(false);
    }
  };

  const allSteps = [
    {
      id: 'welcome',
      title: 'Welcome',
      component: (
        <div className="onboarding-step-content">
          <div className="welcome-content">
            <img 
              src="/ugc3-logo.png" 
              alt="UGC3 Logo" 
              className="welcome-logo"
            />
            <h1 className="brand-name">Welcome</h1>
            <p className="selection-text">Let's set up your brand profile</p>
            <div className="ugc3-brand-badge-inline">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
              </svg>
              Platform
            </div>
            <div className="welcome-message">
              <p>We're excited to help you connect with talented UK creators and grow your brand through authentic UGC content.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'account-type',
      title: 'Account Type',
      component: (
        <div className="onboarding-step-content">
          <div className="account-type-content">
            <h2 className="account-type-title">Are you an Agency or Brand?</h2>
            <p className="account-type-subtitle">Select the option that best describes you</p>
            
            <div className="account-type-options">
              <button
                className={`account-type-card ${accountType === 'brand' ? 'selected' : ''}`}
                onClick={() => setAccountType('brand')}
              >
                <div className="account-type-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="9" x2="15" y2="9"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                </div>
                <h3 className="account-type-name">Brand</h3>
                <p className="account-type-description">
                  I represent a single brand and want to work with creators
                </p>
                {accountType === 'brand' && (
                  <div className="account-type-check">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                )}
              </button>
              
              <button
                className={`account-type-card ${accountType === 'agency' ? 'selected' : ''}`}
                onClick={() => setAccountType('agency')}
              >
                <div className="account-type-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <h3 className="account-type-name">Agency</h3>
                <p className="account-type-description">
                  I manage multiple brands and clients
                </p>
                {accountType === 'agency' && (
                  <div className="account-type-check">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'details',
      title: 'Details',
      component: (
        <div className="onboarding-step-content">
          <div className="details-content">
            <div className="details-card">
              <h2 className="details-title">Tell us about your {accountType === 'agency' ? 'agency' : 'brand'}</h2>
              
              <div className="form-group">
                <label htmlFor="brand-name">Your Name <span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="brand-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="brand-company">{accountType === 'agency' ? 'Agency' : 'Brand'} Name <span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  id="brand-company"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder={`Enter your ${accountType === 'agency' ? 'agency' : 'brand'} name`}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="website">Company Website</label>
                <input
                  type="url"
                  id="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://www.example.com"
                  className="form-input"
                />
              </div>
              
              {accountType === 'brand' && (
                <div className="form-group">
                  <label htmlFor="product-service">Main Product/Service <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    id="product-service"
                    value={productService}
                    onChange={(e) => setProductService(e.target.value)}
                    placeholder="e.g., Skincare products, Fitness app, Coffee brand"
                    className="form-input"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'experience',
      title: 'Experience',
      component: (
        <div className="onboarding-step-content">
          <div className="experience-content">
            <div className="experience-card">
              <h2 className="experience-title">Tell us about your experience</h2>
              
              <div className="form-group">
                <label htmlFor="hear-about-us">How did you hear about us? <span className="required-asterisk">*</span></label>
                <select
                  id="hear-about-us"
                  value={hearAboutUs}
                  onChange={(e) => setHearAboutUs(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select an option</option>
                  <option value="social-media">Social Media</option>
                  <option value="google-search">Google Search</option>
                  <option value="friend-referral">Friend/Referral</option>
                  <option value="industry-event">Industry Event</option>
                  <option value="podcast">Podcast</option>
                  <option value="blog-article">Blog/Article</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="worked-with-creators">Have you worked with UGC creators before? <span className="required-asterisk">*</span></label>
                <select
                  id="worked-with-creators"
                  value={workedWithCreators}
                  onChange={(e) => setWorkedWithCreators(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select an option</option>
                  <option value="yes-regularly">Yes, regularly</option>
                  <option value="yes-occasionally">Yes, occasionally</option>
                  <option value="once-or-twice">Once or twice</option>
                  <option value="no-never">No, never</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'budget-goals',
      title: 'Budget & Goals',
      component: (
        <div className="onboarding-step-content">
          <div className="budget-goals-content">
            <div className="budget-goals-card">
              <h2 className="budget-goals-title">Budget & Goals</h2>
              <p className="budget-goals-subtitle">Help us understand your needs</p>
              
              <div className="form-group">
                <label htmlFor="monthly-budget">Monthly Budget Range <span className="required-asterisk">*</span></label>
                <select
                  id="monthly-budget"
                  value={monthlyBudget}
                  onChange={(e) => setMonthlyBudget(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select a range</option>
                  <option value="under-1000">Under £1,000</option>
                  <option value="1000-2500">£1,000 - £2,500</option>
                  <option value="2500-5000">£2,500 - £5,000</option>
                  <option value="5000-10000">£5,000 - £10,000</option>
                  <option value="10000-25000">£10,000 - £25,000</option>
                  <option value="over-25000">Over £25,000</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>What are your main goals? <span className="required-asterisk">*</span></label>
                <p className="field-subtitle">Select all that apply</p>
                <div className="goals-container">
                  {[
                    { id: 'brand-awareness', label: 'Brand Awareness' },
                    { id: 'content-library', label: 'Build Content Library' },
                    { id: 'conversions', label: 'Drive Conversions/Sales' },
                    { id: 'engagement', label: 'Increase Engagement' },
                    { id: 'product-launch', label: 'Product Launch' },
                    { id: 'testimonials', label: 'Generate Testimonials' },
                    { id: 'other', label: 'Other' }
                  ].map(goal => (
            <button
                      key={goal.id}
              type="button"
                      className={`goal-option-btn ${goals.includes(goal.id) ? 'selected' : ''}`}
                      onClick={() => handleGoalToggle(goal.id)}
                    >
                      {goal.label}
                      {goals.includes(goal.id) && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
            </button>
          ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'profile',
      title: 'Profile Image',
      component: (
        <div className="onboarding-step-content">
          <div className="profile-photo-content">
            <div className="profile-photo-card">
              <h2 className="profile-photo-title">Upload Profile Image <span className="required-asterisk">*</span></h2>
              <p className="profile-photo-subtitle">Add a professional image that represents your {accountType === 'agency' ? 'agency' : 'brand'}</p>
              
              <div className="upload-area">
                {profileImage ? (
                  <div className="photo-preview">
                    <img src={URL.createObjectURL(profileImage)} alt="Profile preview" className="preview-image" />
                    <div className="photo-overlay">
                      <button className="remove-photo-btn" onClick={() => setProfileImage(null)}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                      <p className="photo-name">{profileImage.name}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="upload-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7,10 12,15 17,10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    </div>
                    <h3 className="upload-title">Drag & drop your image here</h3>
                    <p className="upload-subtitle">or click to browse files</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setProfileImage(e.target.files[0])}
                      className="file-input"
                      id="profile-photo-input"
                      ref={fileInputRef}
                    />
                    <button
                      type="button"
                      className="upload-button"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose File
                    </button>
                  </>
                )}
              </div>
              {!profileImage && (
                <p className="upload-note">JPG, PNG up to 5MB</p>
              )}
            </div>
          </div>
        </div>
      )
    }
  ];

  // Filter out null steps (like niches for agencies)
  const steps = allSteps.filter(step => step.component !== null);

  if (loading) {
    return (
      <div className="client-onboarding-wrapper">
        <div className="onboarding-container">
          <div className="loading">Setting up your brand profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="client-onboarding-wrapper">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {toast.type === 'error' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
              )}
            </div>
            <span className="toast-message">{toast.message}</span>
          </div>
        </div>
      )}

      <div className="onboarding-container">
        {/* Progress Bar */}
        {currentStep > 0 && (
          <div className="onboarding-progress">
            <div className="progress-bar-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                ></div>
              </div>
              <div className="progress-text">
                Step {currentStep + 1} of {steps.length}
              </div>
            </div>
          </div>
        )}
        
        {/* Step Content */}
        <div className="onboarding-form">
          {steps[currentStep]?.component}
          
          {/* Navigation */}
          <div className="step-navigation">
            {currentStep > 0 && (
              <button 
                className="btn-secondary" 
                onClick={handlePrevious}
              >
                Previous
              </button>
            )}
            {currentStep < steps.length - 1 ? (
              <button 
                className="btn-primary" 
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
              <button 
                className="btn-primary" 
                onClick={handleNext}
                disabled={uploading}
              >
                {uploading ? 'Completing Setup...' : 'Complete Setup'}
        </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
